import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ClipboardList, 
  AlertTriangle, 
  Scale, 
  Users, 
  Calendar, 
  CheckCircle, 
  XCircle,
  Eye,
  Edit,
  Trash2,
  Search,
  Plus,
  TrendingUp,
  FileText,
  MapPin,
  User,
  Loader2,
  X,
  Image
} from 'lucide-react';

import {Clock} from 'lucide-react'
import Swal from 'sweetalert2';

function ViewInspection() {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [myInspectionCount,setMyInspectionCount]=useState("")
  const [editFormData, setEditFormData] = useState({
    violation: '',
    action: '',
    status: ''
  });

  // Setup axios instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    headers: { 'Accept': 'application/json' }
  });

  // Add token to requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Fetch inspections from API
  const fetchInspections = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/inspectorsInspection');
     
      // Handle different response structures
      let inspectionData = [];
      if (response.data.success) {
        inspectionData = response.data.data || response.data.inspections || [];
        setMyInspectionCount(response.data.myInspectionCount)
      } else if (response.data.data) {
        inspectionData = response.data.data;
      } else if (Array.isArray(response.data)) {
        inspectionData = response.data;
      } else if (response.data.inspections) {
        inspectionData = response.data.inspections;
      }
      
      // Transform data to match your component's expected format
      const formattedInspections = inspectionData.map(inspection => ({
        id: inspection.id || `INS-${Date.now()}-${Math.random()}`,
        date: inspection.inspection_date = new Date().toDateString(),
        inspector: inspection.inspector_name,
        trader: inspection.trader_name || inspection.trader?.name || 'Unknown',
        tin: inspection.tin || inspection.trader?.tin || 'N/A',
        commodity: inspection.commodity_checked || inspection.commodity || 'N/A',
        violation: inspection.violation_type || inspection.violation || 'No Violation',
        action: inspection.action_taken || inspection.action || 'No Action',
        status: inspection.status || 'Pending',
        location: `${inspection.sub_city || inspection.subcity || ''} ${inspection.woreda || ''}`.trim() || 'Unknown',
        region: inspection.region || 'Unknown',
        city: inspection.city || 'Unknown',
        sub_city: inspection.sub_city || 'Unknown',
        woreda: inspection.woreda || 'Unknown',
        business_license: inspection.business_license || 'NA',
        phone: inspection.phone || 'Unknown',
        violation_type: inspection.violation_type || 'Unknown',
        lat: inspection.latitude || 0,
        lng: inspection.longitude || 0,
        quantity: inspection.quantity_checked,
        unit: inspection.unit,
        inspector_id: inspection.inspector_id,
        trader_id: inspection.trader_id,
        

        // Add photo fields
        photo_scene: inspection.photo_scene || null,
        photo_evidence: inspection.photo_evidence || null,
        photo_license: inspection.photo_license || null,
        // Add searchable fields
        searchableText: `${inspection.trader_name || ''} ${inspection.commodity_checked || ''} ${inspection.violation_type || ''} ${inspection.id || ''} ${inspection.inspector_name || ''}`.toLowerCase()
      }));
      
      setInspections(formattedInspections);
    } catch (err) {
      console.error('Error fetching inspections:', err);
      setError(err.response?.data?.message || 'Failed to load inspections');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load inspection data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInspections();
  }, []);

  // IMPROVED SEARCH FUNCTIONALITY
  const filteredInspections = useMemo(() => {
    if (!searchTerm.trim()) {
      return inspections;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    return inspections.filter(item => {
      // Search across multiple fields
      return (
        (item.id && item.id.toString().toLowerCase().includes(searchLower)) ||
        (item.trader && item.trader.toLowerCase().includes(searchLower)) ||
        (item.commodity && item.commodity.toLowerCase().includes(searchLower)) ||
        (item.violation && item.violation.toLowerCase().includes(searchLower)) ||
        (item.inspector && item.inspector.toLowerCase().includes(searchLower)) ||
        (item.status && item.status.toLowerCase().includes(searchLower)) ||
        (item.action && item.action.toLowerCase().includes(searchLower)) ||
        (item.tin && item.tin.toLowerCase().includes(searchLower)) ||
        (item.location && item.location.toLowerCase().includes(searchLower))
      );
    });
  }, [inspections, searchTerm]);

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
  };

  // ────────── STATISTICS ──────────
  const stats = useMemo(() => {
    const total = inspections.length;
    const withViolations = inspections.filter(i => i.violation !== 'No Violation' && i.violation !== 'N/A').length;
    const penalties = inspections.filter(i => i.action === 'Penalty Issued').length;
    const uniqueTraders = new Set(inspections.map(i => i.trader)).size;
    const pendingReview = inspections.filter(i => i.status === 'Pending' || i.status === 'pending').length;
    const completed = inspections.filter(i => i.status === 'Completed' || i.status === 'completed').length;
    
    // Get current month inspections
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const thisMonth = inspections.filter(i => {
      const inspectionDate = new Date(i.date);
      return inspectionDate.getMonth() === currentMonth && 
             inspectionDate.getFullYear() === currentYear;
    }).length;
    
    // Get today's inspections
    const today = new Date().toISOString().split('T')[0];
    const todayInspections = inspections.filter(i => i.date === today).length;
    
    // Get recent inspections (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recent = inspections.filter(i => new Date(i.date) >= sevenDaysAgo).length;
    
    return { 
      total, 
      withViolations, 
      penalties, 
      uniqueTraders,
      pendingReview,
      completed,
      thisMonth,
      todayInspections,
      recent
    };
  }, [inspections]);

  const openViewModal = (inspection) => {
    setSelectedInspection(inspection);
    setIsViewModalOpen(true);
  };

  const HandleAddInspection = () => {
    navigate('/admin/addinspection');
  };

  const HandleInspect = () => {
    navigate('/admin/inspectionpage');
  };

  const openEditModal = (inspection) => {
    setSelectedInspection(inspection);
    setEditFormData({
      violation: inspection.violation,
      action: inspection.action,
      status: inspection.status
    });
    setIsEditModalOpen(true);
  };

  const openPhotoModal = (photoUrl, photoType) => {
    setSelectedPhoto({ url: photoUrl, type: photoType });
    setIsPhotoModalOpen(true);
  };





  const handleDelete = async (id) => {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Are you sure?',
    text: 'You want to delete this item.',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel'
  });

  if (result.isConfirmed) {
    try {
      const response = await api.delete(`/inspections/${id}`);

      // If your Laravel doesn't return success, just assume success
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Inspection deleted successfully',
        timer: 2000,
        showConfirmButton: false
      });

      fetchInspections();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete inspection'
      });
    }
  }
};

  const handleUpdate = async () => {
    try {
      const response = await api.put(`/inspections/${selectedInspection.id}`, editFormData);
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Inspection updated successfully',
          timer: 2000
        });
        // Refresh the list
        fetchInspections();
        setIsEditModalOpen(false);
        setSelectedInspection(null);
        setEditFormData({ violation: '', action: '', status: '' });
      }
    } catch (error) {
      console.error('Error updating inspection:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update inspection'
      });
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch(lowerStatus) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'pending review':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to get full image URL
const getImageUrl = (path) => {
  if (!path) return null;

  console.log('Original path from DB:', path);   // ← Add this

  if (path.startsWith('http')) return path;

  const filename = path.split(/[/\\]/).pop();
  const finalUrl = `https://api.cdaethi.org/inspections/${filename}`;

  return finalUrl;
};
 
 



   if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          {/* Spinner */}
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          
          {/* Inner icon */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-6 h-6 text-indigo-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        
        <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Your Data</h3>
        <p className="mt-2 text-gray-500">Please wait while we fetch your data...</p>
        
        {/* Loading steps */}
        <div className="mt-6 space-y-2">
          {['Fetching inspections', 'Loading statistics', 'Preparing charts'].map((step, index) => (
            <div key={step} className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-indigo-600 animate-pulse' : 'bg-gray-300'}`}></div>
              <span className={index === 0 ? 'text-indigo-600' : ''}>{step}</span>
              {index === 0 && (
                <svg className="w-3 h-3 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={fetchInspections}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <div className="flex items-center gap-2 md:gap-3">
                <ClipboardList className="h-8 w-8 md:h-10 md:w-10 text-indigo-600 flex-shrink-0" />
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    Inspection Reports
                  </h1>
                  <p className="text-sm md:text-base text-gray-600 mt-1">View and manage all inspection records</p>
                </div>
              </div>
            </div>

        
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-700 text-sm font-medium">Total Inspections</p>
                <p className="text-2xl font-bold text-gray-800">{myInspectionCount}</p>
              </div>
              <ClipboardList className="w-10 h-10 text-indigo-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-700 text-sm font-medium">Violations Found</p>
                <p className="text-2xl font-bold text-red-600">{stats.withViolations}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-700 text-sm font-medium">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendingReview}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-700 text-sm font-medium">Unique Traders</p>
                <p className="text-2xl font-bold text-green-600">{stats.uniqueTraders}</p>
              </div>
              <Users className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Search Bar - IMPROVED */}
        <div className="mb-6">

             
              <button
                onClick={HandleInspect}
                className="w-full sm:w-auto bg-indigo-600 text-white px-4 mb-4 py-2.5 md:px-6 md:py-3 mb-4 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg mb-4"
              >
                <Plus className="h-5 w-5" />
                Add Inspection
              </button>
        
          <div className="relative w-full max-w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-4 h-4 md:w-5 md:h-5" />
            <input
              type="text"
              placeholder="Search by ID, trader, commodity, inspector, violation, status, or action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-10 py-2 md:py-2.5 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none bg-white text-sm md:text-base"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Search results count */}
          
          {searchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              Found {filteredInspections.length} result{filteredInspections.length !== 1 ? 's' : ''} for "{searchTerm}"
            </div>
          )}
        </div>
        

        {/* Table */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[768px] md:min-w-full w-full divide-y divide-gray-200">
              <thead className="bg-indigo-700">
                <tr>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Date</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Inspector</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Trader</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">TIN</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Region</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">City</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Sub City</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell">Woreda</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Latitude</th>
                                    <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Longitude</th>
                                                      <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">Phone</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Violation Type</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">License</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Photos</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Action Taken</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                  <th className="px-3 md:px-6 py-3 md:py-4 text-right text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInspections.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="px-6 py-12 text-center text-gray-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>{searchTerm ? `No inspections found matching "${searchTerm}"` : 'No inspections found'}</p>
                    </td>
                  </tr>
                ) : (
                  filteredInspections.map((item) => (
                    <tr key={item.id} className="hover:bg-indigo-50 transition-colors duration-150">
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap font-medium text-black text-xs md:text-sm">{item.id}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.date}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm hidden sm:table-cell">{item.inspector}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm font-medium">{item.trader}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.tin}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.region}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm hidden lg:table-cell">{item.city}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.sub_city}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm hidden md:table-cell">{item.woreda}</td>
  
                                   <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.lat}</td>

                                   <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm">{item.lng}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-black text-xs md:text-sm hidden sm:table-cell">{item.phone}</td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className="bg-red-700 text-white rounded-md   px-2 py-2 rounded-md text-sm font-medium">
                          {item.violation_type}
                        </span>
                      </td>
                     
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {item.photo_evidence && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.photo_evidence), 'Evidence Photo')}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                              title="View Evidence Photo"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                    
                          {!item.photo_evidence && !item.photo_license && !item.photo_scene && (
                            <span className="text-gray-400 text-xs">No photos</span>
                          )}
                        </div>
                      </td>

                        <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                       
                          {item.photo_license && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.photo_license), 'License Photo')}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition"
                              title="View License Photo"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {item.photo_scene && (
                            <button
                              onClick={() => openPhotoModal(getImageUrl(item.photo_scene), 'Scene Photo')}
                              className="p-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition"
                              title="View Scene Photo"
                            >
                              <Image className="w-4 h-4" />
                            </button>
                          )}
                          {!item.photo_evidence && !item.photo_license && !item.photo_scene && (
                            <span className="text-gray-400 text-xs">No photos</span>
                          )}
                        </div>
                      </td>


                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className="bg-indigo-700 text-white rounded-md   px-2 py-2 rounded-md text-sm font-medium">
                          {item.action}
                        </span>
                      </td>


                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-right text-xs md:text-sm font-medium">
                        <div className="flex justify-end space-x-1 md:space-x-2">
                          <button
                            onClick={() => openViewModal(item)}
                            className="p-1.5 md:p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="p-1.5 md:p-1 text-blue-600 hover:text-blue-800 transition rounded-lg hover:bg-blue-100"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className={`p-1.5 md:p-1 transition rounded-lg ${
                              deleteConfirmId === item.id
                                ? 'bg-red-600 text-white px-2 py-1.5 md:px-2 md:py-1'
                                : 'text-red-600 hover:text-red-800 hover:bg-red-100'
                            }`}
                            title={deleteConfirmId === item.id ? 'Confirm Delete' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Modal */}
        {isViewModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-indigo-700">Inspection Details</h2>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-indigo-700 text-sm font-medium mb-1">Inspection ID</label>
                      <p className="text-black">{selectedInspection.id}</p>
                    </div>
                    <div>
                      <label className="block text-indigo-700 text-sm font-medium mb-1">Date</label>
                      <p className="text-black">{selectedInspection.date ? new Date(selectedInspection.date).toDateString() : 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">Inspector</label>
                    <p className="text-black">{selectedInspection.inspector}</p>
                  </div>
                  
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">Trader</label>
                    <p className="text-black">{selectedInspection.trader}</p>
                  </div>
                  
                  {selectedInspection.tin && selectedInspection.tin !== 'N/A' && (
                    <div>
                      <label className="block text-indigo-700 text-sm font-medium mb-1">TIN</label>
                      <p className="text-black">{selectedInspection.tin}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">Commodity</label>
                    <p className="text-black">{selectedInspection.commodity}</p>
                  </div>

                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">City</label>
                    <p className="text-black">{selectedInspection.city}</p>
                  </div>
                  
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">Action Taken</label>
                    <p className="text-black">{selectedInspection.action}</p>
                  </div>
                  
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-1">Status</label>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedInspection.status)}`}>
                      {selectedInspection.status}
                    </span>
                  </div>

                  {/* Photos Section in View Modal */}
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-2">Photos</label>
                    <div className="grid grid-cols-3 gap-3">
                      {selectedInspection.photo_evidence && (
                        <div className="text-center">
                          <button
                            onClick={() => openPhotoModal(getImageUrl(selectedInspection.photo_evidence), 'Evidence Photo')}
                            className="w-full"
                          >
                            <img 
                              src={getImageUrl(selectedInspection.photo_evidence)} 
                              alt="Evidence"
                              className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition"
                            />
                            <p className="text-xs text-gray-600 mt-1">Evidence</p>
                          </button>
                        </div>
                      )}
                      {selectedInspection.photo_license && (
                        <div className="text-center">
                          <button
                            onClick={() => openPhotoModal(getImageUrl(selectedInspection.photo_license), 'License Photo')}
                            className="w-full"
                          >
                            <img 
                              src={getImageUrl(selectedInspection.photo_license)} 
                              alt="License"
                              className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition"
                            />
                            <p className="text-xs text-gray-600 mt-1">License</p>
                          </button>
                        </div>
                      )}
                      {selectedInspection.photo_scene && (
                        <div className="text-center">
                          <button
                            onClick={() => openPhotoModal(getImageUrl(selectedInspection.photo_scene), 'Scene Photo')}
                            className="w-full"
                          >
                            <img 
                              src={getImageUrl(selectedInspection.photo_scene)} 
                              alt="Scene"
                              className="w-full h-24 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition"
                            />
                            <p className="text-xs text-gray-600 mt-1">Scene</p>
                          </button>
                        </div>
                      )}
                      {!selectedInspection.photo_evidence && !selectedInspection.photo_license && !selectedInspection.photo_scene && (
                        <p className="text-gray-400 text-sm">No photos available</p>
                      )}
                    </div>
                  </div>
                  
                  {selectedInspection.location && selectedInspection.location !== 'Unknown' && (
                    <div>
                      <label className="block text-indigo-700 text-sm font-medium mb-1">Location</label>
                      <p className="text-black">{selectedInspection.location}</p>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Photo Modal for Fullscreen View */}
        {isPhotoModalOpen && selectedPhoto && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl w-full">
              <button
                onClick={() => setIsPhotoModalOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition"
              >
                <X size={32} />
              </button>
              <div className="bg-white rounded-xl overflow-hidden">
                <div className="p-4 bg-gray-100 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">{selectedPhoto.type}</h3>
                </div>
                <div className="p-4 flex justify-center">
                  <img 
                    src={selectedPhoto.url} 
                    alt={selectedPhoto.type}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                    }}
                  />
                </div>
                <div className="p-4 bg-gray-50 flex justify-end">
                  <button
                    onClick={() => setIsPhotoModalOpen(false)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedInspection && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-xl font-bold text-indigo-700 mb-4">Edit Inspection</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-2">Violation Type</label>
                    <select
                      name="violation"
                      value={editFormData.violation}
                      onChange={handleEditChange}
                      className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="No Violation">No Violation</option>
                      <option value="Expired Product">Expired Product</option>
                      <option value="Price Violation">Price Violation</option>
                      <option value="Trading Without License">Trading Without License</option>
                      <option value="Short Weight">Short Weight</option>
                      <option value="Counterfeit Product">Counterfeit Product</option>
                      <option value="Unsafe Product">Unsafe Product</option>
                      <option value="False Advertising">False Advertising</option>
                      <option value="Tax Evasion">Tax Evasion</option>
                      <option value="Other Violation">Other Violation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-2">Action Taken</label>
                    <select
                      name="action"
                      value={editFormData.action}
                      onChange={handleEditChange}
                      className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="Verbal Warning">Verbal Warning</option>
                      <option value="Written Warning">Written Warning</option>
                      <option value="Penalty Issued">Penalty Issued</option>
                      <option value="Product Seized">Product Seized</option>
                      <option value="License Suspended">License Suspended</option>
                      <option value="Business Closed">Business Closed</option>
                      <option value="No Action">No Action</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-indigo-700 text-sm font-medium mb-2">Status</label>
                    <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditChange}
                      className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                      <option value="Failed">Failed</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ViewInspection;